import React, { useState, useEffect } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import { TrashIcon } from "@heroicons/react/24/outline";
import { Bars3Icon } from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../../utils/ResourceLoading";
import ProConfirmation from "../../../utils/ProConfirmation";
import { TWclassNames } from "../../../utils/Div";
import { PMNotice } from "../../../utils/notifications";

// Removed Material-UI imports - using only Tailwind CSS

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  // some basic styles to make the items look a bit nicer
  userSelect: "none",
  padding: 0,
  margin: `0 0 ${grid}px 0`,

  // change background colour if dragging
  background: isDragging ? "lightgrey" : null,

  // styles we need to apply on draggables
  ...draggableStyle,
});

export default function UserNextOfKinTable(props) {
  const { setFieldValue, values, isLoading, drag } = props;

  const name = props.name ? props.name : "user_next_of_kins";

  const rows = values[name];

  // Relationship types enum from Ruby model
  const RELATIONSHIP_TYPES = [
    "SPOUSE",
    "PARTNER",
    "PARENT",
    "CHILD",
    "SIBLING",
  ];

  const columns = [
    { id: "drag", label: "", minWidth: 10, maxWidth: 5, active: drag },
    {
      id: "name",
      label: "Navn",
      minWidth: 150,
      align: "left",
      active: true,
    },
    {
      id: "relationship_type",
      label: "Relasjon",
      minWidth: 120,
      align: "left",
      active: true,
    },
    {
      id: "phone_number",
      label: "Telefon",
      minWidth: 120,
      align: "left",
      active: true,
    },
    {
      id: "address",
      label: "Adresse",
      minWidth: 200,
      align: "left",
      active: true,
    },
  ];

  const orderItems = (rows) => {
    var array = [];

    if (!rows) return;

    rows.map((row, i) => {
      const rowParams = {
        ...row,
        row_order: i,
        draggableId: "id" + row.id,
      };
      array.push(rowParams);
    });
    return array;
  };

  const [items, setItems] = useState(rows?.length > 0 ? orderItems(rows) : []);

  useEffect(() => {
    // Filter out items that is "is_editable" is false
    const filteredItems = items.filter((item) => item.is_editable !== false);

    setFieldValue(name, filteredItems);
  }, [items]);

  const addItemRow = (type) => {
    const newRow = {
      // Generate a random ID for the new row until one is made in the API
      id: null,
      row_order: items.length,
      // Generating a fake ID for draggable, so that we are able to move the row before saving.
      draggableId: "id" + items.length + 1,
    };
    setItems((prevState) => [...prevState, newRow]);
  };

  const onItemRowChange = (e, data) => {
    if (typeof e === "function") {
      e.preventDefault();
    }

    let prevItems = [...items];
    let item = {
      ...prevItems[data?.row_order],
      [e.target.name]: e.target.value,
    };
    prevItems[data?.row_order] = item;
    setItems(prevItems);
  };

  const onDragEnd = (result) => {
    // dropped outside the list
    if (!result.destination) {
      return;
    }

    const newItems = reorder(
      items,
      result.source.index,
      result.destination.index
    );

    setItems(orderItems(newItems));
  };

  const [selectedItemRow, setSelectedItemRow] = useState();

  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);

  const handleDeleteRow = () => {
    setDeleteConfirmationOpen(false);
    // Delete selectedItem row from array
    if (selectedItemRow) {
      const newArray = items.map((item) =>
        item.id === selectedItemRow.id ||
        item.draggableId === selectedItemRow.draggableId
          ? { ...item, delete: true }
          : item
      );
      setItems(orderItems(newArray));
    }
  };

  const deleteRow = (item) => {
    setSelectedItemRow(item);
    setDeleteConfirmationOpen(true);
  };

  const itemRow = (item, provided, snapshot) => {
    if (item.delete) return null; // Hide the item if it is marked as deleted
    return (
      <div
        key={item.id}
        ref={provided.innerRef}
        {...provided.draggableProps}
        {...provided.dragHandleProps}
        style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
        className={`flex items-center border-b border-gray-100 py-6 px-6 hover:bg-gray-50 transition-colors duration-200 ${
          snapshot.isDragging ? "bg-blue-50 shadow-lg" : ""
        }`}
      >
        {/* Drag Handle */}
        {drag ? (
          <div className="flex-shrink-0 w-10 flex items-center justify-center mr-2">
            <Bars3Icon className="h-5 w-5 text-gray-400 cursor-move hover:text-gray-600" />
          </div>
        ) : null}

        {/* Name */}
        <div className="flex-1 min-w-0 px-3">
          <input
            required
            disabled={item?.is_editable === false}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors"
            value={item?.name || ""}
            name="name"
            onChange={(e) => onItemRowChange(e, item)}
            placeholder="Fullt navn"
          />
        </div>

        {/* Relationship Type */}
        <div className="flex-shrink-0 w-36 px-3">
          <select
            disabled={item?.is_editable === false}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors"
            value={item?.relationship_type || ""}
            name="relationship_type"
            onChange={(e) => onItemRowChange(e, item)}
          >
            <option value="">Velg relasjon</option>
            {RELATIONSHIP_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </div>

        {/* Phone Number */}
        <div className="flex-shrink-0 w-36 px-3">
          <input
            disabled={item?.is_editable === false}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors"
            value={item?.phone_number || ""}
            name="phone_number"
            onChange={(e) => onItemRowChange(e, item)}
            placeholder="Telefonnummer"
          />
        </div>

        {/* Address */}
        <div className="flex-1 min-w-0 px-3">
          <input
            disabled={item?.is_editable === false}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:cursor-not-allowed transition-colors"
            value={item?.address || ""}
            name="address"
            onChange={(e) => onItemRowChange(e, item)}
            placeholder="Adresse"
          />
        </div>

        {/* Delete Button */}
        <div className="flex-shrink-0 w-12 flex justify-center">
          <button
            onClick={() => deleteRow(item)}
            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors duration-200"
            title="Slett rad"
          >
            <TrashIcon className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <ResourceLoading />;
  }

  if (!items || items.length === 0) {
    return (
      <>
        <PMNotice
          title="Ingen pårørende lagt til"
          description="Du kan legge til pårørende"
        />
        <button
          onClick={() => addItemRow()}
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Ny rad
        </button>
      </>
    );
  }

  return (
    <React.Fragment>
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Table Header */}
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-6 py-4 border-b border-gray-200">
          <div className="flex items-center">
            {columns.map((column) =>
              column.active ? (
                <div
                  key={column.id}
                  className={`px-3 text-left text-sm font-semibold text-gray-700 ${
                    column.id === "drag"
                      ? "w-10 mr-2"
                      : column.id === "name"
                      ? "flex-1 min-w-0"
                      : column.id === "relationship_type"
                      ? "w-36"
                      : column.id === "phone_number"
                      ? "w-36"
                      : column.id === "address"
                      ? "flex-1 min-w-0"
                      : "w-12"
                  }`}
                >
                  {column.label}
                </div>
              ) : null
            )}
          </div>
        </div>

        {/* Table Body */}
        <DragDropContext onDragEnd={onDragEnd}>
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                className="min-h-[200px]"
              >
                {items?.map((item, index) => (
                  <Draggable
                    key={item.id}
                    draggableId={item.draggableId}
                    index={index}
                  >
                    {(provided, snapshot) => itemRow(item, provided, snapshot)}
                  </Draggable>
                ))}
                {provided.placeholder}

                {/* Empty state when no items */}
                {items?.length === 0 && (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <div className="text-4xl mb-4">👥</div>
                    <p className="text-lg font-medium">
                      Ingen pårørende registrert
                    </p>
                    <p className="text-sm">
                      Klikk &quot;Ny rad&quot; for å legge til en pårørende
                    </p>
                  </div>
                )}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* Table Footer */}
        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
          <button
            onClick={() => addItemRow()}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors duration-200"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Legg til pårørende
          </button>
        </div>
      </div>

      <ProConfirmation
        severity="confirmation"
        open={deleteConfirmationOpen}
        message={"Er du sikker på at du vil slette denne raden?"}
        action="delete"
        module="order_item"
        // item_id={confirmation.item_id}
        yesCallback={handleDeleteRow}
        cancelCallback={() => setDeleteConfirmationOpen(false)}
      />
    </React.Fragment>
  );
}

UserNextOfKinTable.defaultProps = {
  drag: false,
};
